<script setup>
import gsap from 'gsap'
import SplitType from 'split-type'
import { useIsMobile } from '~~/composables/useIsMobile'
import { useMenuStore } from '~/store/menu.js'
import { headlineAnimation, eyebrowAnimation } from '~/utils/animations'

const background = ref(null)
const isHovering = ref(null)

const store = useMenuStore()
const isOpen = computed(() => store.isOpen)
const isMobile = useIsMobile()

/**
 * Animation
 */
let tl
let tl2
const el = ref(null)
const SWIPE_DURATION = 750
const swipeAngle = computed(() => (isMobile.value ? 60 : 200))

function onEnter() {
  const fromPath = `polygon(
      0% calc(100% + ${swipeAngle.value}px),
      100% 100%,
      100% 100%,
      0% 100%
    )`
  const toPath = `polygon(
      0% calc(0% + 0px),
      100% 0%,
      100% 100%,
      0% 100%
    )`
  tl?.kill() // todo: if it's mid-transition, just reverse the current one
  tl = gsap.timeline({ clearProps: true }).fromTo(
    el.value,
    { clipPath: fromPath },
    {
      clipPath: toPath,
      duration: SWIPE_DURATION / 1000,
      ease: 'power2.in',
    }
  )

  tl2?.kill()
  /**
   * Headline link line splits
   */
  const headlineLinks = [
    ...el.value.querySelectorAll('.global-menu__nav-headline'),
  ]
  const splits = headlineLinks.map(
    (headlineEl) => new SplitType(headlineEl, { types: 'lines' })
  )
  const splitLines = splits.reverse().map((splits) => splits.lines)

  /**
   * Eyebrow char splits
   */
  const eyebrows = [...el.value.querySelectorAll('.global-menu__eyebrow')]
  const charSplits = eyebrows.map(
    (eyebrowEl) => new SplitType(eyebrowEl, { types: 'chars' })
  )
  const splitChars = charSplits.reverse().map((splits) => splits.chars)

  /**
   * Positions for '.animate--flip-in' elements
   */
  const els = [...el.value.querySelectorAll('.animate--flip-in')]
  const positions = {}
  const xPositions = []
  const yPositions = []
  els.forEach((flipEl, index) => {
    const { left, top } = flipEl.getBoundingClientRect()

    if (xPositions.indexOf(left) === -1) {
      xPositions.push(left)
    }
    if (yPositions.indexOf(top) === -1) {
      yPositions.push(top)
    }

    positions[index] = { top, left }
  })
  xPositions.sort()
  yPositions.sort()

  tl2 = gsap
    .timeline({ delay: 0.5, clearProps: true })
    .from(
      el.value.querySelector('.global-menu__divider'),
      {
        scaleX: 0,
        transformOrigin: 'right',
        duration: 0.75,
        ease: 'power2.inOut',
      },
      0
    )
    .from(
      el.value.querySelectorAll('.global-menu__social'),
      {
        scale: 0,
        ease: 'power2.out',
        duration: 1,
        stagger: {
          each: 0.1,
          from: 'end',
        },
      },
      0
    )
    .from(
      el.value.querySelectorAll('.animate--flip-in'),
      {
        ...headlineAnimation,
        stagger: (index) => {
          const pos = positions[index]
          const xPosIndex = xPositions.indexOf(pos.left)
          const yPosIndex = yPositions.indexOf(pos.top)
          const xPos = (xPosIndex + 1) / xPositions.length
          const yPos = (yPosIndex + 1) / yPositions.length
          return (1 - yPos) * (1 - xPos) * 1
        },
      },
      0.25
    )
    .from(
      splitLines,
      {
        ...headlineAnimation,
        stagger: 0.1,
      },
      0.1
    )
    .from(splitChars, eyebrowAnimation, 0.75)
    .from(
      el.value.querySelector('.global-menu__language-selector'),
      {
        opacity: 0,
        duration: 1,
      },
      0.5
    )
    .call(() => {
      splits.forEach((split) => split.revert())
      charSplits.forEach((split) => split.revert())
    })
}

function onLeave() {
  const fromPath = `polygon(
      0% 0%,
      100% 0%,
      100% 100%,
      0% calc(100% + ${swipeAngle.value}px)
    )`
  const toPath = `polygon(
      0% 0%,
      100% 0%,
      100% 0%,
      0% calc(0% + 0px)
    )`
  tl?.kill()
  tl = gsap
    .timeline({ clearProps: true })
    .fromTo(
      el.value,
      {
        clipPath: fromPath,
      },
      {
        clipPath: toPath,
        duration: SWIPE_DURATION / 1000,
        ease: 'power2.in',
      }
    )
    .call(() => {
      if (tl2) {
        tl2.seek(tl2.duration())
        tl2.kill()
      }
    })
}

/**
 * Mouse events
 */
function onMouseEnter(slug) {
  background.value = slug
  isHovering.value = true
}

function onMouseLeave() {
  isHovering.value = false
}

function onClick() {
  store.isOpen = false
}

/**
 * Scroll Lock
 */
watch(isOpen, (value) => {
  if (value) {
    document.body.classList.add('no-scroll')
  } else {
    document.body.classList.remove('no-scroll')
  }
})
</script>

<template>
  <Transition
    name="menu"
    :duration="{ enter: SWIPE_DURATION, leave: SWIPE_DURATION }"
    @enter="onEnter"
    @leave="onLeave"
  >
    <div
      v-show="isOpen"
      ref="el"
      class="global-menu"
      :class="[isHovering ? 'is-hovering' : null, `is-hovering--${background}`]"
    >
      <div class="global-menu__background spacecraft-fleet" />

      <div class="global-menu__background spaceport-america" />

      <div class="global-menu__background spaceflight-experience" />

      <div class="global-menu__wrapper container">
        <nav class="global-menu__nav">
          <ul>
            <li
              @mouseenter="onMouseEnter('spacecraft-fleet')"
              @mouseleave="onMouseLeave"
            >
              <NuxtLink
                class="global-menu__main-link gtm-vgms-header-menu-spacecraft-fleet"
                :to="localePath('spacecraft-fleet')"
                :class="
                  isHovering
                    ? background === 'spacecraft-fleet'
                      ? ''
                      : 'is-fading'
                    : ''
                "
                @click="onClick"
              >
                <p class="global-menu__eyebrow">
                  {{ $t('menu.fleetEyebrow') }}
                </p>
                <div class="global-menu__nav-headline-container">
                  <p
                    class="global-menu__nav-headline text__headline-4 text--gradient"
                    v-html="$t('menu.fleetTitle')"
                  />
                </div>
              </NuxtLink>
            </li>
            <li
              @mouseenter="onMouseEnter('spaceport-america')"
              @mouseleave="onMouseLeave"
            >
              <NuxtLink
                class="gtm-vgms-header-menu-spaceport-america"
                :to="localePath('spaceport-america')"
                :class="
                  isHovering
                    ? background === 'spaceport-america'
                      ? ''
                      : 'is-fading'
                    : ''
                "
                @click="onClick"
              >
                <p class="global-menu__eyebrow">
                  {{ $t('menu.spaceportEyebrow') }}
                </p>
                <div class="global-menu__nav-headline-container">
                  <p
                    class="global-menu__nav-headline text__headline-4 text--gradient"
                    v-html="$t('menu.spaceportTitle')"
                  />
                </div>
              </NuxtLink>
            </li>
            <li
              @mouseenter="onMouseEnter('spaceflight-experience')"
              @mouseleave="onMouseLeave"
            >
              <NuxtLink
                class="gtm-vgms-header-menu-flight-experience"
                :to="localePath('spaceflight-experience')"
                :class="
                  isHovering
                    ? background === 'spaceflight-experience'
                      ? ''
                      : 'is-fading'
                    : ''
                "
                @click="onClick"
              >
                <p class="global-menu__eyebrow">
                  {{ $t('menu.flightEyebrow') }}
                </p>
                <div class="global-menu__nav-headline-container">
                  <p
                    class="global-menu__nav-headline text__headline-4 text--gradient"
                    v-html="$t('menu.flightTitle')"
                  />
                </div>
              </NuxtLink>
            </li>
          </ul>
        </nav>

        <div class="global-menu__divider" />

        <nav class="global-menu__subnav">
          <div class="global-menu__subnav-column">
            <p class="global-menu__eyebrow">
              <span>{{ $t('menu.About') }}</span>
            </p>

            <ul>
              <li>
                <NuxtLink
                  class="gtm-vgms-header-menu-news"
                  :to="localePath('/news')"
                  @click="onClick"
                >
                  <span class="animate--flip-in">{{ $t('menu.News') }}</span>
                </NuxtLink>
              </li>
              <li>
                <NuxtLink
                  class="gtm-vgms-header-menu-contact-us"
                  :to="localePath('/contact')"
                  @click="onClick"
                >
                  <span class="animate--flip-in">{{
                    $t('menu["Contact Us"]')
                  }}</span>
                </NuxtLink>
              </li>
              <li>
                <NuxtLink
                  class="gtm-vgms-header-menu-gallery"
                  :to="localePath('/gallery')"
                  @click="onClick"
                >
                  <span class="animate--flip-in">{{
                    $t('menu["Gallery"]')
                  }}</span>
                </NuxtLink>
              </li>
              <li>
                <NuxtLink
                  class="gtm-vgms-header-menu-faq"
                  :to="localePath('/faq')"
                  @click="onClick"
                >
                  <span class="animate--flip-in">{{ $t('menu["FAQ"]') }}</span>
                </NuxtLink>
              </li>
            </ul>
          </div>

          <div class="global-menu__subnav-column">
            <p class="global-menu__eyebrow">
              <span>{{ $t('menu["Company"]') }}</span>
            </p>

            <ul>
              <li>
                <NuxtLink
                  class="gtm-vgms-header-menu-galactic-unite"
                  :to="localePath('/galactic-unite')"
                  @click="onClick"
                >
                  <span class="animate--flip-in">
                    <span>{{ $t('menu["Galactic Unite"]') }}</span>
                  </span>
                </NuxtLink>
              </li>

              <!-- <li>
                <NuxtLink
                  class="gtm-vgms-header-menu-esg-impact-report"
                  :to="localePath('/')"
                  @click="onClick"
                >
                  <span class="animate--flip-in">{{
                    $t('menu["ESG Impact Report"]')
                  }}</span>
                </NuxtLink>
              </li> -->

              <!-- <li>
                <a
                  href="#"
                  class="is-external"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span class="animate--flip-in"
                    ><span>{{ $t('menu["Merch"]') }}</span>
                    <Icon name="external-link" />
                  </span>
                </a>
              </li> -->
              <li>
                <a
                  class="is-external animate--flip-in gtm-vgms-header-menu-work-with-us"
                  href="https://vgcareers.virgingalactic.com/global/en"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span class="animate--flip-in"
                    ><span>{{ $t('menu["Work With Us"]') }}</span>
                    <Icon name="external-link" />
                  </span>
                </a>
              </li>
            </ul>
          </div>

          <div class="global-menu__subnav-column">
            <p class="global-menu__eyebrow">
              <span>{{ $t('menu["More For"]') }}</span>
            </p>

            <ul>
              <li>
                <NuxtLink
                  class="animate--flip-in gtm-vgms-header-menu-researchers"
                  :to="localePath('/researchers')"
                  @click="onClick"
                >
                  <span class="animate--flip-in">
                    <span>{{ $t('menu["Researchers"]') }}</span>
                  </span>
                </NuxtLink>
              </li>
              <li>
                <a
                  class="is-external animate--flip-in gtm-vgms-header-menu-investors"
                  href="https://investors.virgingalactic.com/overview/default.aspx"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span class="animate--flip-in">
                    <span>{{ $t('menu["Investors"]') }}</span>
                    <Icon name="external-link" />
                  </span>
                </a>
              </li>
              <li>
                <a
                  class="is-external animate--flip-in gtm-vgms-header-menu-press"
                  href="https://press.virgingalactic.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span class="animate--flip-in"
                    ><span>{{ $t('menu["Press"]') }}</span>
                    <Icon name="external-link" />
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </nav>

        <ul class="global-menu__footer">
          <li
            class="global-menu__footer-column global-menu__footer-column-left"
          >
            <ul>
              <li>
                <NuxtLink
                  class="gtm-vgms-header-menu-privacy-policy"
                  :to="localePath('/privacy-policy')"
                  @click="onClick"
                >
                  <p class="global-menu__eyebrow">
                    <span>{{ $t('menu["Privacy Policy"]') }}</span>
                  </p>
                </NuxtLink>
              </li>
              <li class="animate--flip-in">
                <NuxtLink
                  class="gtm-vgms-header-menu-terms"
                  :to="localePath('/terms-and-conditions')"
                  @click="onClick"
                >
                  <p class="global-menu__eyebrow">
                    <span>{{ $t('menu["Terms of Use"]') }}</span>
                  </p>
                </NuxtLink>
              </li>
              <li class="animate--flip-in">
                <NuxtLink
                  class="gtm-vgms-header-menu-cookie-notice"
                  :to="localePath('/cookie-notice')"
                  @click="onClick"
                >
                  <p class="global-menu__eyebrow">
                    <span>{{ $t('menu["Cookie Notice"]') }}</span>
                  </p>
                </NuxtLink>
              </li>
              <li class="animate--flip-in">
                <NuxtLink
                  class="gtm-vgms-header-menu-ada-statement"
                  :to="localePath('/ada-statement')"
                  @click="onClick"
                >
                  <p class="global-menu__eyebrow">
                    <span>{{ $t('menu["ADA Statement"]') }}</span>
                  </p>
                </NuxtLink>
              </li>
            </ul>
          </li>
          <li
            class="global-menu__footer-column global-menu__footer-column-center"
          >
            <ul class="global-menu__socials">
              <li class="global-menu__social">
                <a
                  class="gtm-vgms-header-menu-instagram"
                  href="https://www.instagram.com/virgingalactic"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span class="u-visually-hidden">Go to Instagram</span>
                  <Icon name="instagram" title="Instagram" />
                </a>
              </li>

              <li class="global-menu__social">
                <a
                  class="gtm-vgms-header-menu-twitter"
                  href="https://twitter.com/virgingalactic"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span class="u-visually-hidden">Go to Twitter</span>
                  <Icon name="twitter" title="Twitter" />
                </a>
              </li>

              <li class="global-menu__social">
                <a
                  class="gtm-vgms-header-menu-youtube"
                  href="https://www.youtube.com/@VirginGalactic"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span class="u-visually-hidden">Go to Youtube</span>
                  <Icon name="youtube" title="Youtube" />
                </a>
              </li>

              <li class="global-menu__social">
                <a
                  class="gtm-vgms-header-menu-facebook"
                  href="https://www.facebook.com/VirginGalactic"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span class="u-visually-hidden">Go to Facebook</span>
                  <Icon name="facebook" title="Facebook" />
                </a>
              </li>

              <li class="global-menu__social">
                <a
                  class="gtm-vgms-header-menu-tiktok"
                  href="https://www.tiktok.com/@virgingalactic"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span class="u-visually-hidden">Go to TikTok</span>
                  <Icon name="tiktok" title="TikTok" />
                </a>
              </li>

              <li class="global-menu__social">
                <a
                  class="global-footer__social-link gtm-vgms-header-menu-linkedin"
                  href="https://www.linkedin.com/company/virgin-galactic"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span class="u-visually-hidden">{{
                    $t('footer.Linkedin')
                  }}</span>
                  <Icon name="linkedin" />
                </a>
              </li>
            </ul>

            <div class="global-menu__divider" />
          </li>

          <li
            class="global-menu__footer-column global-menu__footer-column-right"
          >
            <LanguageSelector class="global-menu__language-selector" />
          </li>
        </ul>

        <div class="global-menu__logo-bg">
          <svg
            width="535"
            height="432"
            viewBox="0 0 535 432"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g style="mix-blend-mode: overlay">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M353.353 331.654H404.424C394.908 300.476 374.973 273.488 347.936 255.179L342.136 173.151C341.879 169.643 340.746 166.254 338.841 163.293L291.877 89.9534C291.849 89.7614 291.819 89.5454 291.785 89.3065C289.994 76.6954 279.107 0 267.795 0H267.748C256.74 0 246.165 72.6227 243.904 88.1518C243.797 88.8836 243.709 89.4887 243.64 89.9534L196.682 163.293C194.778 166.254 193.645 169.643 193.387 173.151L187.587 255.179C204.363 243.791 223.395 236.124 243.396 232.697L257.454 281.926H278.122L292.153 232.717C312.144 236.142 331.166 243.801 347.936 255.179L353.353 331.654ZM182.229 331.654H131.152C140.668 300.476 160.603 273.488 187.64 255.179L182.229 331.654ZM101.422 446.028H84.0034L124.094 359.813L164.204 446.028H146.785L140.195 431.57H108.013L101.422 446.028ZM124.094 396.273L114.979 416.277L133.209 416.271L124.094 396.273ZM491.894 404.652C491.85 397.448 494.598 390.504 499.565 385.272C501.981 382.711 504.897 380.669 508.135 379.275C511.372 377.88 514.861 377.161 518.388 377.163C524.752 377.182 530.911 379.405 535.813 383.452V364.985C530.61 362.753 525.001 361.617 519.337 361.647C507.758 361.581 496.623 366.084 488.361 374.173C484.257 378.183 481.001 382.973 478.787 388.261C476.573 393.548 475.445 399.225 475.47 404.954C475.451 410.579 476.565 416.15 478.748 421.336C480.931 426.523 484.137 431.219 488.177 435.145C496.261 443.07 507.156 447.494 518.493 447.454C524.449 447.392 530.334 446.162 535.813 443.833V425.248C530.955 429.506 524.724 431.88 518.256 431.938C503.717 431.938 491.894 419.695 491.894 404.652ZM220.784 430.663H193.446V363.191H177.359V445.995H213.666L220.784 430.663ZM461.3 363.204H445.206L445.199 446.008H461.3V363.204ZM369.836 364.985C364.621 362.748 358.998 361.611 353.32 361.647C341.74 361.581 330.603 366.084 322.338 374.173C318.244 378.188 314.999 382.98 312.795 388.268C310.591 393.555 309.473 399.229 309.506 404.954C309.486 410.579 310.599 416.149 312.781 421.336C314.963 426.522 318.168 431.218 322.206 435.145C330.291 443.07 341.186 447.494 352.523 447.454C358.479 447.393 364.364 446.163 369.843 443.833V425.248C364.969 429.523 358.711 431.899 352.22 431.938C337.681 431.938 325.857 419.695 325.857 404.652C325.816 397.448 328.564 390.506 333.529 385.272C335.953 382.703 338.88 380.658 342.129 379.263C345.378 377.868 348.88 377.153 352.417 377.163C358.779 377.182 364.937 379.405 369.836 383.452V364.985ZM430.535 363.204H383.228V378.53H398.835V445.995H414.929V378.53H430.535V363.204ZM245.261 446.008H227.836L267.933 359.813L308.063 446.008H290.644L284.053 431.55H251.852L245.261 446.008ZM267.933 396.273L258.812 416.258L277.048 416.271L267.933 396.273ZM41.3294 447.447C70.7563 447.447 81.2156 422.56 81.2156 405.474C81.2439 402.607 81.1074 399.742 80.807 396.891H52.7245L45.4749 412.407H64.436C64.436 414.72 59.8753 432.063 41.4217 432.063C29.1699 432.063 15.8174 421.265 15.8174 405C15.8174 389.215 26.8434 377.163 42.2653 377.136C48.6195 377.216 54.7682 379.393 59.7501 383.327L66.5647 368.672C59.3168 364.095 50.9168 361.66 42.3378 361.647C17.4123 361.647 0 381.388 0 404.751C0 426.503 15.9689 447.454 41.3294 447.447Z"
                fill="url(#paint0_linear_2115_8646)"
              />
            </g>
            <defs>
              <linearGradient
                id="paint0_linear_2115_8646"
                x1="252"
                y1="505"
                x2="497"
                y2="182.5"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="white" />
                <stop offset="1" stop-color="white" stop-opacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss">
@use 'sass:math';

// Vendors
@import 'styles/vendors/sass-mq/mq';

// Utils
@import 'styles/utils/variables';
@import 'styles/utils/functions';
@import 'styles/utils/timing-functions';

.global-menu {
  background: radial-gradient(
      26.01% 25.18% at 113.75% 3.94%,
      rgba(50, 14, 65, 0.4) 0%,
      rgba(50, 14, 65, 0) 100%
    ),
    radial-gradient(
      81.27% 77.77% at 93.58% 18.4%,
      rgba(172, 45, 206, 0.4) 0%,
      rgba(200, 10, 251, 0) 100%
    ),
    radial-gradient(
      52.32% 52.32% at 41.25% -13.29%,
      rgba(22, 39, 197, 0.4) 0%,
      rgba(21, 42, 244, 0) 100%
    ),
    #0d041e;
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: map-get($z-layers, 'menu');
  overflow-y: auto;

  &__logo-bg {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 535px;
    height: 447px;
    overflow: hidden;
    opacity: 0.018;
    pointer-events: none;

    @include mq($until: 'l') using ($until) {
      display: none;
    }

    svg {
      position: relative;
      top: 16px;
      display: block;
      width: 100%;
    }
  }

  &__background {
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s $ease-out-quad;

    &.spacecraft-fleet {
      background-image: radial-gradient(
          84.41% 196% at 124.55% 74.39%,
          rgba(200, 10, 251, 0.15) 0%,
          rgba(200, 10, 251, 0) 100%
        ),
        radial-gradient(
          37.94% 37.94% at 34.79% -17.94%,
          rgba(21, 42, 244, 0.22) 0%,
          rgba(21, 42, 244, 0) 100%
        ),
        radial-gradient(
          63.43% 42.86% at 65.83% 16.56%,
          rgba(13, 6, 29, 0) 0%,
          #0d061d 100%
        ),
        url('~/assets/images/menu/spacecraft-fleet.png');
      background-position: center center, center center, center center,
        center top;
      background-size: 100%, 100%, 100%, 100%;
      background-color: #0d061d;

      @include mq($from: 'l') using ($from) {
        background-size: 100%, 100%, 100%, cover;
        background-position: center center, center center, center center,
          center -15vh;
      }
    }

    &.spaceport-america {
      background-image: radial-gradient(
          84.41% 196% at 124.55% 74.39%,
          rgba(200, 10, 251, 0.15) 0%,
          rgba(200, 10, 251, 0) 100%
        ),
        radial-gradient(
          37.94% 37.94% at 34.79% -17.94%,
          rgba(21, 42, 244, 0.22) 0%,
          rgba(21, 42, 244, 0) 100%
        ),
        radial-gradient(
          63.43% 42.86% at 65.83% 16.56%,
          rgba(13, 6, 29, 0) 0%,
          #0d061d 100%
        ),
        url('~/assets/images/menu/spaceport-america.png');
      background-color: #0d061d;
      // background-size: 100%, 100%, 100%, 100%%;
      background-position: center center, center center, center center,
        right top;
    }

    &.spaceflight-experience {
      background-image: radial-gradient(
          84.41% 196% at 124.55% 74.39%,
          rgba(200, 10, 251, 0.15) 0%,
          rgba(200, 10, 251, 0) 100%
        ),
        radial-gradient(
          37.94% 37.94% at 34.79% -17.94%,
          rgba(21, 42, 244, 0.22) 0%,
          rgba(21, 42, 244, 0) 100%
        ),
        radial-gradient(
          63.43% 42.86% at 65.83% 16.56%,
          rgba(13, 6, 29, 0) 0%,
          #0d061d 100%
        ),
        url('~/assets/images/menu/spaceflight-experience.png');
      background-color: #0d061d;
      background-position: center center, center center, center center,
        center -20vh;
    }
  }

  &.is-hovering {
    &.is-hovering--spacecraft-fleet {
      .global-menu__background.spacecraft-fleet {
        opacity: 1;
      }
    }

    &.is-hovering--spaceport-america {
      .global-menu__background.spaceport-america {
        opacity: 1;
      }
    }

    &.is-hovering--spaceflight-experience {
      .global-menu__background.spaceflight-experience {
        opacity: 1;
      }
    }
  }

  &__wrapper {
    padding-top: 140px;
    padding-bottom: 72px;
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
    z-index: 1;

    @include mq($from: 'l') using($from) {
      min-height: 100%;
      padding-bottom: 55px;
    }
  }

  &__eyebrow {
    font-size: 10px;
    font-weight: $font-weight-4G;
    line-height: math.div(15, 10);
    letter-spacing: 0.27em;
    text-transform: uppercase;
    color: rgba($brand-white, 0.5);
    transition: color 0.25s;
    font-kerning: none;

    a:hover & {
      color: $brand-white;
    }
  }

  &__nav {
    margin-top: auto;
    // flex-grow: 1;

    @include mq($from: 'l') using($from) {
      padding: 0 gs(1, 1, 'l');
    }
  }

  &__nav ul {
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 68px;
    justify-content: space-around;
    align-items: center;

    @include mq($from: 'l') using($from) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__nav li:not(:first-child) .global-menu__nav-headline-container::before {
    @include mq($from: 'l') using($from) {
      content: '';
      background-color: rgba($brand-white, 0.1);
      border-radius: 50%;
      display: block;
      width: 4px;
      height: 4px;
      position: absolute;
      top: 50%;
      left: -2px;
      pointer-events: none;
    }
  }

  &__nav a {
    display: flex;
    flex-direction: column;
    align-items: center;

    @include mq($from: 'l') using($from) {
      padding: 80px 0;
    }
  }

  &__nav .is-fading p {
    opacity: 0.5;
  }

  &__nav &__eyebrow {
    display: none;

    @include mq($from: 'l') using($from) {
      display: block;
    }
  }

  &__nav p {
    text-align: center;
    width: 100%;
  }

  &__nav-headline-container {
    position: relative;
    width: 100%;
  }

  &__nav-headline {
    .line {
      will-change: transform, opacity;
      backface-visibility: hidden;
      transform: translateZ(0);
    }
    @include mq($from: 'l') using($from) {
      margin-top: 32px;
    }
  }

  &__divider {
    margin-top: 20px;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    width: 100%;
    height: 40px;
    flex-shrink: 0;
    overflow: hidden;

    @include mq($from: 'l') using($from) {
      margin-top: 66px;
    }

    &::before,
    &::after {
      content: '';
      display: block;
      grid-area: 1 / 1;
      justify-self: center;
      align-self: center;
    }

    &::before {
      background: radial-gradient(
        50% 50% at 50% 50%,
        #c80afb 0%,
        rgba(200, 10, 251, 0.63) 31.77%,
        rgba(200, 10, 251, 0.36) 60.94%,
        rgba(200, 10, 251, 0) 99.94%
      );
      width: gs(4, 5, 'xxxs');
      height: 80px;
      opacity: 0.07;

      @include mq($from: 'l') using($from) {
        width: gs(18, 17, $from);
      }
    }

    &::after {
      background: linear-gradient(
        89.94deg,
        rgba(125, 96, 180, 0) -2.15%,
        #7d60b4 50.7%,
        rgba(125, 96, 180, 0) 99.85%
      );
      width: gs(6, 5, 'xxxs');
      height: 4px;

      @include mq($from: 'l') using($from) {
        background: linear-gradient(
          89.94deg,
          rgba(125, 96, 180, 0) -2.15%,
          #7d60b4 50.7%,
          rgba(125, 96, 180, 0.947036) 53.3%,
          rgba(125, 96, 180, 0) 99.85%
        );
        width: 100%;
      }
    }
  }

  &__subnav {
    margin-top: 56px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex-shrink: 0;

    @include mq($from: 'l') using($from) {
      margin-top: 80px;
      padding: 0 gs(1, 1, $from);
      flex-direction: row;
    }
  }

  &__subnav-column {
    width: 100%;
    @include mq($from: 'l') using($from) {
      width: gs(4, 3, $from);
    }
  }

  &__subnav-column:not(:first-child) {
    margin-top: 48px;

    @include mq($from: 'l') using($from) {
      margin-top: 0;
      margin-left: gs(0, 1, $from);
    }
  }

  &__subnav &__eyebrow {
    text-align: center;

    @include mq($from: 'l') using($from) {
      text-align: left;
    }
  }

  &__subnav ul {
    margin-top: 24px;

    @include mq($from: 'l') using($from) {
      margin-top: 32px;
    }
  }

  &__subnav li:not(:first-child) {
    margin-top: 12px;

    @include mq($from: 'l') using($from) {
      margin-top: 6px;
    }
  }

  &__subnav a {
    color: $brand-white;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: color 0.25s;

    @include mq($from: 'l') using($from) {
      justify-content: flex-start;
    }

    &:hover,
    &:focus {
      color: $brand-magenta;
    }
  }

  &__subnav .is-external .svg {
    color: #c80afb;
    margin-left: 6px;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  &__footer {
    margin-top: 80px;
    padding: 0 gs(1, 1, 'l');
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;

    @include mq($from: 'l') using($from) {
      flex-direction: row;
    }
  }

  &__footer-column {
    width: 100%;
    flex-shrink: 0;
  }

  &__footer-column {
    @include mq($from: 'l') using($from) {
      margin-left: gs(0, 1, 'l');
    }

    &:first-child {
      margin-left: 0;
    }
  }

  &__footer-column-left {
    margin-top: 32px;
    order: 2;

    @include mq($from: 'l') using($from) {
      margin-top: 0;
      width: gs(16, 15, $from);
      order: 1;
    }
  }

  &__footer-column-left ul {
    display: grid;
    grid-template-rows: repeat(2, 1fr);
    grid-template-columns: 1fr auto 1fr;
    row-gap: 20px;
    align-items: center;

    @include mq($from: 'l') using($from) {
      display: flex;
      justify-content: flex-start;
    }

    &::before,
    &::after {
      content: '  •  ';
      font-size: 10px;
      font-weight: $font-weight-3G;
      line-height: math.div(14, 10);
      letter-spacing: 0.5em;
      pointer-events: none;
      white-space: pre;
      order: 2;
      flex-shrink: 0;

      @include mq($from: 'l') using($from) {
        display: none;
      }
    }

    &::after {
      order: 5;
    }
  }

  &__footer-column-left li {
    flex-shrink: 0;
    display: flex;

    @include mq($from: 'l') using($from) {
      width: gs(4, 4, $from);
    }

    a {
      flex-shrink: 0;
    }
  }

  &__footer-column-left li:nth-of-type(1) {
    justify-content: flex-end;
    order: 1;

    @include mq($from: 'l') using($from) {
      justify-content: flex-start;
    }
  }

  &__footer-column-left li:nth-of-type(2) {
    order: 3;
  }

  &__footer-column-left li:nth-of-type(3) {
    justify-content: flex-end;
    order: 4;

    @include mq($from: 'l') using($from) {
      justify-content: flex-start;
    }
  }

  &__footer-column-left li:nth-of-type(4) {
    order: 6;
  }

  &__footer-column-center {
    order: 1;

    @include mq($from: 'l') using($from) {
      order: 2;
      width: gs(4, 3, $from);
    }
  }

  &__footer-column-center &__divider {
    margin-top: 0;

    @include mq($from: 'l') using($from) {
      display: none;
    }
  }

  &__footer-column-right {
    order: 3;

    @include mq($from: 'l') using($from) {
      width: gs(2, 1, $from);
      margin-left: auto;
    }
  }

  &__footer a {
    color: rgba($brand-white, 0.5);

    .no-touchevents &:hover,
    .no-touchevents &:focus {
      color: $brand-white;
    }
  }

  &__footer a span {
    font-weight: $font-weight-5G;
    // color: currentColor;
  }

  &__socials {
    display: flex;
    justify-content: center;
    align-items: center;

    @include mq($from: 'l') using($from) {
      justify-content: flex-start;
    }
  }

  &__social:not(:first-child) {
    margin-left: 36px;

    @include mq($from: 'l') using($from) {
      margin-left: 16px;
    }
  }

  &__social a {
    display: flex;
    transition: color 0.25s;

    &:hover {
      color: $brand-white;
    }
  }

  &__social .svg {
    color: $brand-white;
    width: 24px;
    height: 24px;

    @include mq($from: 'l') using($from) {
      color: currentColor;
      width: 14px;
      height: 14px;
    }
  }

  &__language-selector {
    display: none; // TEMPORARY until languages get added back

    margin-top: 32px;
    @include mq($from: 'l') using ($from) {
      margin-top: 0;
    }
  }

  .animate--flip-in {
    will-change: transform, opacity;
  }
}
</style>
